import React from 'react';

import Modal from '~/components/modals/modal';
import CartItem from '~/components/cart/cart-item';
import Button from '~/components/button';
import { Form } from '~/components/form';
import { useCart } from '~/hooks/use-cart';
import { useCartMutations } from '~/features/cart/cart-actions';
import { useFlashMessage } from '~/features/flash-messages/flash-message-actions';
import { unique } from '~/utils/functions';
import { skusToCartItems } from '~/features/cart/cart';

import styles from '~/components/modals/modal.module.scss';

const CartValidationModal = ({
  onClose,
  isOpen,
  outOfStockSkus = [],
  restrictedSkus = [],
}) => {
  return (
    <Modal
      allowClose={false}
      isOpen={isOpen}
      className={styles.cartValidationModal}
    >
      <CartValidationModalContent
        onClose={onClose}
        outOfStockSkus={outOfStockSkus}
        restrictedSkus={restrictedSkus}
      />
    </Modal>
  );
};

export default CartValidationModal;

function CartValidationModalContent({
  onClose,
  outOfStockSkus,
  restrictedSkus,
}) {
  const { showFlashMessage } = useFlashMessage();
  const { cart } = useCart();
  const { removeMultipleFromCart, confirmCart } = useCartMutations();

  const productsToRemove = skusToCartItems(
    cart,
    unique([...outOfStockSkus, ...restrictedSkus])
  );
  const outOfStockProducts = skusToCartItems(cart, unique(outOfStockSkus));
  const restrictedProducts = skusToCartItems(cart, unique(restrictedSkus));

  const handleRemoveItems = async () => {
    try {
      await removeMultipleFromCart(productsToRemove);
      await confirmCart(cart.uuid);
      onClose();
    } catch (err) {
      onClose();
      showFlashMessage(
        'There was an issue removing those items from your cart. You can try to remove them yourself, or come back later.',
        'error'
      );
    }
  };

  return (
    <div className={styles.cartValidationContent}>
      <h2>We're sorry about this...</h2>

      {outOfStockProducts?.length > 0 ? (
        <>
          <p>
            The following items have gone out of stock and will be removed from
            your cart:
          </p>
          <ul className={styles.productsList}>
            {outOfStockProducts.map((product) => (
              <CartItem
                key={product.sku.skuId}
                cartItem={product}
                interactive={false}
              />
            ))}
          </ul>
        </>
      ) : null}

      {restrictedProducts?.length > 0 ? (
        <>
          <p>
            The following items are not available for sale in your state and
            will be removed from your cart:
          </p>
          <ul className={styles.productsList}>
            {restrictedProducts.map((product) => (
              <CartItem
                key={product.sku.skuId}
                cartItem={product}
                interactive={false}
              />
            ))}
          </ul>
        </>
      ) : null}

      <Form onSubmit={handleRemoveItems}>
        {({ isSubmitting }) => (
          <Button type="submit" fullWidth isLoading={isSubmitting}>
            Remove Items
          </Button>
        )}
      </Form>
    </div>
  );
}
